/* eslint-disable react/prop-types */
// Internal presentational leaves below receive the griffel `classes` object and
// the `i18n` instance as props (accessing nested members that PropTypes can't
// meaningfully describe); the exported BudgetSuggestionTableContainer keeps full
// propTypes. Same convention as the UC budget-suggestion-panel consumer.
import React, { useContext } from 'react';
import { noop } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  FluentProvider,
  webLightTheme,
  RadioGroup,
  Radio,
  InfoLabel,
  Skeleton,
  SkeletonItem,
  tokens,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidatedBudgetInput } from '@bingads-webui-campaign-react/budget-component-v2';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getBudgetDetailKeys } from './budget-item';
import { useBudgetSuggestionSelection } from './use-budget-suggestion-selection';
import {
  BudgetSuggestionTiers,
  BudgetTierTitles,
  BudgetTypesTooltip,
  BudgetDetailsKeysText,
  BudgetDetailKeys,
  CustomBudget,
} from '../model/constants';

// Left column footprint of a Fluent v9 medium Radio (indicator + gap). The header
// row has no radio, so its leading cell is indented by this amount to keep the
// metric columns aligned with the tier rows.
const RADIO_INDENT = 36;
const TYPE_COL_WIDTH = 200;

// Display order for the metric columns in the table layout. The resolved keys
// from getBudgetDetailKeys are reordered to this sequence for presentation only
// (the shared constants used by the default/PMax layout are left untouched).
const TABLE_METRIC_ORDER = [
  BudgetDetailKeys.Impressions,
  BudgetDetailKeys.Clicks,
  BudgetDetailKeys.Conversions,
  BudgetDetailKeys.CTR,
  BudgetDetailKeys.CostPerClick,
  BudgetDetailKeys.CostPerConversion,
];

// Cost-type metrics get the account currency appended to their column header.
const COST_METRIC_KEYS = [BudgetDetailKeys.CostPerClick, BudgetDetailKeys.CostPerConversion];

const orderTableMetricKeys = keys => [...keys].sort((a, b) => {
  const ai = TABLE_METRIC_ORDER.indexOf(a);
  const bi = TABLE_METRIC_ORDER.indexOf(b);
  return (ai === -1 ? TABLE_METRIC_ORDER.length : ai) - (bi === -1 ? TABLE_METRIC_ORDER.length : bi);
});

export const getStyles = () => ({
  // Plain block that fills the available width. No overflow is set on either axis
  // so the table never produces a horizontal or vertical scrollbar; the metric
  // columns flex to share whatever room remains after the fixed type column.
  container: {
    width: '100%',
    position: 'relative',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    boxSizing: 'border-box',
    paddingTop: '4px',
    paddingBottom: '10px',
    // Mirror the Fluent Radio label content box so the header columns line up
    // with the tier rows below: the tier rows sit inside a Radio whose indicator
    // footprint (16px + 2*8px margin = 32px) plus the label's 4px padding-left
    // pushes the content in by 36px, and the label adds 8px padding-right.
    paddingLeft: `${RADIO_INDENT}px`,
    paddingRight: '8px',
  },
  // Invisible stand-in for the tier-row divider: keeps the header metric columns
  // aligned with the values while showing no line after "Budget types".
  headerDivider: {
    flex: '0 0 auto',
    width: '1px',
    alignSelf: 'stretch',
    marginLeft: '16px',
    marginRight: '16px',
    backgroundColor: 'transparent',
  },
  typeCell: {
    flex: `0 0 ${TYPE_COL_WIDTH}px`,
    width: `${TYPE_COL_WIDTH}px`,
    boxSizing: 'border-box',
  },
  divider: {
    flex: '0 0 auto',
    width: '1px',
    alignSelf: 'stretch',
    marginLeft: '16px',
    marginRight: '16px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  metricsRow: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'row',
    minWidth: 0,
  },
  metricCol: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  headerCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    height: tokens.lineHeightBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground2,
    // `HelpIcon` renders a bare `<a class="helpbutton">` with no children; its
    // question-mark glyph normally comes from legacy global CSS that Fluent V2
    // surfaces don't load. Without it the anchor is invisible yet still
    // tabbable and activatable, so keyboard users land on nothing and can open
    // a pop-up by accident (ADO 11012030, MAS 2.4.3). Paint the MDL2 glyph so
    // the control is visible, matching the other Fluent V2 hosts of HelpIcon.
    '& .helpbutton': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      fontWeight: tokens.fontWeightRegular,
      color: tokens.colorNeutralForeground3,
      cursor: 'pointer',
      '&::before': {
        content: '"\\E9CE"',
        fontFamily: '"MsAds MDL2 Assets"',
      },
      '&:hover': {
        color: tokens.colorNeutralForeground2,
      },
      // The anchor is focusable, so it needs a visible focus indicator.
      '&:focus-visible': {
        outline: `2px solid ${tokens.colorStrokeFocus2}`,
        borderRadius: tokens.borderRadiusSmall,
      },
    },
    '& .fui-InfoLabel__label': {
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      lineHeight: tokens.lineHeightBase300,
      color: tokens.colorNeutralForeground2,
    },
  },
  rowRoot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  customRowSelected: {
    paddingBottom: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})`,
  },
  typeContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
  },
  radioTitle: {
    fontSize: tokens.fontSizeBase300,
  },
  budgetDay: {
    fontSize: tokens.fontSizeBase200,
  },
  metricValue: {
    fontSize: tokens.fontSizeBase300,
  },
  textSelected: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  textUnselected: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  skeleton: {
    height: '20px',
    width: '80px',
    display: 'inline-block',
  },
  selectedRow: {
    // Draw the 2px selection ring with an inset box-shadow rather than a border: a
    // border (even with border-box) shrinks the content box and shifts the row's inner
    // content — including the divider — 2px right versus the borderless unselected rows,
    // so their dividers wouldn't line up. box-shadow paints the same ring without
    // affecting layout, keeping the divider aligned across selected/unselected tiers.
    boxShadow: `inset 0 0 0 2px ${tokens.colorBrandStroke1}`,
    borderRadius: '8px',
    boxSizing: 'border-box',
  },
  radio: {
    width: '100%',
    '& .fui-Label': {
      width: '100%',
    },
  },
  radioGroup: {
    width: '100%',
    rowGap: '2px',
  },
  // Custom-budget input tweaks: hide the Fluent Label (the "average daily budget"
  // subtext is redundant here) and cap the input width so it stays compact. The
  // negative margin pulls the input left so its edge lines up with the radio
  // circle: the label content sits 36px in (indicator footprint + label padding)
  // while the circle's left edge is at its 8px margin, so shift left by 28px.
  customBudgetInput: {
    position: 'absolute',
    left: '8px',
    bottom: '4px',
    zIndex: 1,
    '& .fui-Label': {
      display: 'none',
    },
    '& .budget-input': {
      maxWidth: '120px',
    },
    // Currency suffix: normal weight, and append "/day" (localized word injected
    // via the --custom-budget-per-day CSS variable set on the wrapper) so the unit
    // reads e.g. "USD/day", matching the tier rows' "$X/day".
    '& .budget-currency': {
      fontWeight: tokens.fontWeightRegular,
    },
    '& .budget-currency::after': {
      content: 'var(--custom-budget-per-day)',
    },
  },
  description: {
    marginTop: '5px',
    marginBottom: '5px',
  },
});

const MetricValueCell = ({
  classes, budgetDetails, metricKey, isLoading, isSelected, i18n,
}) => (
  <div className={classNames(classes.metricCol, 'budget-metric-cell')} data-budget-metric="true">
    <span className={classNames(classes.metricValue, isSelected ? classes.textSelected : classes.textUnselected)}>
      {isLoading ? (
        <Skeleton className={classes.skeleton} appearance="translucent" animation="wave" as="span" aria-label={i18n.getString(_TL_('Loading'))}>
          <SkeletonItem className={classes.skeleton} as="span" size={16} />
        </Skeleton>
      ) : (budgetDetails && budgetDetails[metricKey] && budgetDetails[metricKey].Range)}
    </span>
  </div>
);

MetricValueCell.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  budgetDetails: PropTypes.shape({}),
  metricKey: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  i18n: PropTypes.shape({}).isRequired,
};

MetricValueCell.defaultProps = {
  budgetDetails: null,
  isLoading: false,
  isSelected: false,
};

export const TableHeaderRow = ({
  classes, budgetDetailsKeys, currency, i18n,
}) => {
  const budgetTypesTooltip = i18n.getString(BudgetTypesTooltip);

  return (
    <div className={classNames(classes.headerRow, 'budget-table-header')} data-budget-row="header">
      <div className={classes.typeCell}>
        <InfoLabel
          className={classes.headerCell}
          info={budgetTypesTooltip}
          infoButton={{ 'aria-label': i18n.getString(_TL_('Learn more about budget types')) }}
          size="small"
        >
          {i18n.getString(_TL_('Budget types'))}
        </InfoLabel>
      </div>
      <div className={classes.headerDivider} />
      <div className={classes.metricsRow}>
        {budgetDetailsKeys.map(key => (
          <div key={key} className={classes.metricCol}>
            <span className={classes.headerCell}>
              {i18n.getString(BudgetDetailsKeysText[key].title)}
              {currency && COST_METRIC_KEYS.indexOf(key) !== -1 ? ` (${currency})` : ''}
              &nbsp;
              <HelpIcon topic={BudgetDetailsKeysText[key].popupId} ariaLabel={i18n.getString(_TL_('Learn more about {{item}}'), { item: BudgetDetailsKeysText[key].helpPopupTitle })} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

TableHeaderRow.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  budgetDetailsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  currency: PropTypes.string,
  i18n: PropTypes.shape({}).isRequired,
};

TableHeaderRow.defaultProps = {
  currency: 'USD',
};

const TierRowLabel = ({
  classes, tier, tierData, budgetDetailsKeys, isLoading, isSelected, currency, i18n,
}) => {
  const { BudgetAmountDaily, BudgetDetails } = tierData || {};

  return (
    <div className={classNames(classes.rowRoot, `budget-item-${tier}`)}>
      <div className={classes.typeCell}>
        <div className={classes.typeContent}>
          <span className={classNames(classes.radioTitle, isSelected ? classes.textSelected : classes.textUnselected)}>{i18n.getString(BudgetTierTitles[tier])}</span>
          <span className={classNames(classes.budgetDay, isSelected ? classes.textSelected : classes.textUnselected)}>
            {isLoading ? (
              <Skeleton className={classes.skeleton} appearance="translucent" animation="wave" as="span" aria-label={i18n.getString(_TL_('Loading'))}>
                <SkeletonItem className={classes.skeleton} as="span" size={16} />
              </Skeleton>
            ) : `${i18n.formatCurrency(BudgetAmountDaily, currency)}/${i18n.getString(_TL_('day'))}`}
          </span>
        </div>
      </div>
      <div className={classes.divider} />
      <div className={classes.metricsRow}>
        {budgetDetailsKeys.map(key => (
          <MetricValueCell key={key} classes={classes} budgetDetails={BudgetDetails} metricKey={key} isLoading={isLoading} isSelected={isSelected} i18n={i18n} />
        ))}
      </div>
    </div>
  );
};

TierRowLabel.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  tier: PropTypes.string.isRequired,
  tierData: PropTypes.shape({}),
  budgetDetailsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  currency: PropTypes.string,
  i18n: PropTypes.shape({}).isRequired,
};

TierRowLabel.defaultProps = {
  tierData: null,
  isLoading: false,
  isSelected: false,
  currency: 'USD',
};

const CustomRowLabel = ({
  classes,
  budgetDetailsKeys,
  isSelected,
  customPerformanceData,
  i18n,
}) => {
  const { BudgetDetails, isLoading } = customPerformanceData || {};

  return (
    <div className={classNames(classes.rowRoot, 'budget-item-CustomBudget', isSelected && classes.customRowSelected)}>
      <div className={classes.typeCell}>
        <div className={classes.typeContent}>
          <span className={classNames(classes.radioTitle, isSelected ? classes.textSelected : classes.textUnselected)}>{i18n.getString(_TL_('Set your own budget'))}</span>
        </div>
      </div>
      <div className={isSelected ? classes.divider : classes.headerDivider} />
      <div className={classes.metricsRow}>
        {budgetDetailsKeys.map(key => (
          <MetricValueCell
            key={key}
            classes={classes}
            budgetDetails={isSelected ? BudgetDetails : null}
            metricKey={key}
            isLoading={isSelected && isLoading}
            isSelected={isSelected}
            i18n={i18n}
          />
        ))}
      </div>
    </div>
  );
};

CustomRowLabel.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  budgetDetailsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  isSelected: PropTypes.bool.isRequired,
  customPerformanceData: PropTypes.shape({}),
  i18n: PropTypes.shape({}).isRequired,
};

CustomRowLabel.defaultProps = {
  customPerformanceData: null,
};

// Table-layout variant of BudgetSuggestionContainer used by the Universal
// Campaign budget step. Presentation only: it reuses the exact same selection +
// validation orchestration (useBudgetSuggestionSelection), metric-column logic
// (getBudgetDetailKeys), and leaf components (ValidatedBudgetInput, HelpIcon,
// Radio) as the default container. It is a separate component so the default
// layout used by PMax and other consumers is entirely unaffected.
export const BudgetSuggestionTableContainer = ({
  data,
  isLoading,
  mode,
  campaignType,
  currency,
  onBudgetValidate,
  onSelectedBudgetChange,
  customPerformanceData,
  defaultBudget,
  bidType,
  minBudget,
  maxBudget,
  showDropdown,
  onSelectCurrency,
  currencies,
  initialSelectedBudget,
  isTargetCpaSet,
  hideDescription,
}) => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  const {
    selectedBudget,
    setSelectedBudget,
    customBudgetResult,
    setCustomBudgetResult,
    hasError,
  } = useBudgetSuggestionSelection({
    data,
    isLoading,
    mode,
    defaultBudget,
    onBudgetValidate,
    onSelectedBudgetChange,
    initialSelectedBudget,
  });

  const budgetDetailsKeys = orderTableMetricKeys(getBudgetDetailKeys({ bidType, campaignType, isTargetCpaSet }));

  if (hasError) {
    return (
      <ValidatedBudgetInput
        validationProps={{
          onValidate: setCustomBudgetResult,
          data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
          initialValue: defaultBudget,
          alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
        }}
        budgetKey="budget"
        currency={currency || 'USD'}
        label={i18n.getString(_TL_('Set the campaign\'s average daily budget'))}
        className="custom-budget-input"
        minBudget={minBudget}
        maxBudget={maxBudget}
        showDropdown={showDropdown}
        onSelectCurrency={onSelectCurrency}
        currencies={currencies}
      />
    );
  }

  return (
    <FluentProvider theme={webLightTheme}>
      {!hideDescription && (
        <div className={classes.description}>
          {i18n.getString(_TL_('The following budget options are based on performance estimates from businesses with similar campaigns, ad settings, and budgets.'))}
        </div>
      )}
      <div className={classes.container} data-budget-table="true">
        <TableHeaderRow classes={classes} budgetDetailsKeys={budgetDetailsKeys} currency={currency} i18n={i18n} />
        {/* Fix for ADO 11092490/11092492/11092494 (MAS 4.1.2 Name, Role, Value),
          ADO 11092491 (MAS 4.1.1 Parsing), and ADO 11092493 (MAS 1.3.1 Info
          and Relationships): use plain radios and keep the editor outside labels. */}
        <RadioGroup
          value={selectedBudget}
          className={classNames('budget-suggestion-group', classes.radioGroup, isLoading ? 'budget-loading' : '')}
          label={i18n.getString(_TL_('Choose an option'))}
          onChange={(e, val) => setSelectedBudget(val.value)}
        >
          {Object.keys(BudgetSuggestionTiers).map(tier => (
            <Radio
              key={tier}
              value={tier}
              label={(
                <TierRowLabel
                  classes={classes}
                  tier={tier}
                  tierData={data && data[tier]}
                  budgetDetailsKeys={budgetDetailsKeys}
                  isLoading={isLoading}
                  isSelected={selectedBudget === tier}
                  currency={currency}
                  i18n={i18n}
                />
              )}
              checked={selectedBudget === tier}
              className={selectedBudget === tier
                ? classNames(classes.selectedRow, classes.radio, `budget-radio-${tier}`, 'selected')
                : classNames(`budget-radio-${tier}`, classes.radio)}
            />
          ))}
          <Radio
            key="CustomBudget"
            value="CustomBudget"
            label={(
              <CustomRowLabel
                classes={classes}
                budgetDetailsKeys={budgetDetailsKeys}
                isSelected={selectedBudget === CustomBudget}
                customPerformanceData={customPerformanceData}
                i18n={i18n}
              />
              )}
            checked={selectedBudget === CustomBudget}
            className={selectedBudget === CustomBudget
                ? classNames(classes.selectedRow, classes.radio, `budget-radio-${CustomBudget}`, 'selected')
                : classNames(`budget-radio-${CustomBudget}`, classes.radio)}
          />
        </RadioGroup>
        {selectedBudget === CustomBudget && (
          <div
            className={classes.customBudgetInput}
            style={{ '--custom-budget-per-day': `"/${i18n.getString(_TL_('day'))}"` }}
          >
            <ValidatedBudgetInput
              validationProps={{
                onValidate: setCustomBudgetResult,
                data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
                initialValue: defaultBudget,
                alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
              }}
              budgetKey="budget"
              currency={currency || 'USD'}
              className="custom-budget-input"
              minBudget={minBudget}
              maxBudget={maxBudget}
              showDropdown={showDropdown}
              onSelectCurrency={onSelectCurrency}
              currencies={currencies}
            />
          </div>
        )}
      </div>
    </FluentProvider>
  );
};

BudgetSuggestionTableContainer.propTypes = {
  data: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  currency: PropTypes.string,
  onBudgetValidate: PropTypes.func.isRequired,
  onSelectedBudgetChange: PropTypes.func,
  customPerformanceData: PropTypes.shape({}),
  defaultBudget: PropTypes.number,
  bidType: PropTypes.string,
  minBudget: PropTypes.number,
  maxBudget: PropTypes.number,
  showDropdown: PropTypes.bool,
  onSelectCurrency: PropTypes.func,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
  initialSelectedBudget: PropTypes.string,
  isTargetCpaSet: PropTypes.bool,
  hideDescription: PropTypes.bool,
};

BudgetSuggestionTableContainer.defaultProps = {
  currency: 'USD',
  onSelectedBudgetChange: noop,
  customPerformanceData: null,
  defaultBudget: undefined,
  bidType: undefined,
  minBudget: undefined,
  maxBudget: undefined,
  showDropdown: false,
  onSelectCurrency: noop,
  currencies: [],
  initialSelectedBudget: null,
  isTargetCpaSet: false,
  hideDescription: false,
};
